import Layout from "../components/Layout";
import { 
  ExternalLink, 
  FileText, 
  Calendar, 
  Users, 
  BookOpen, 
  FolderOpen, 
  MessageSquare,
  AlertCircle,
  Shield,
  ClipboardCheck,
  Mail
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";

const resources = [
  {
    category: "Compliance Management",
    items: [
      {
        title: "CompliMind (CMS)",
        description: "Single source of truth for policies, contracts, and compliance documents. Access all your compliance materials in one centralized location.",
        url: "https://CMS.CompliSun.com",
        icon: Shield,
        featured: true,
      },
      {
        title: "Board Disclosures",
        description: "Investment Adviser Disclosure Forms required by SEC regulations",
        url: "https://www.complisun.com/pointe-capital-investment-adviser-disclosure-form/",
        icon: FileText,
      },
      {
        title: "Issue Management",
        description: "Track, manage, and resolve compliance issues and incidents",
        url: "https://www.complisun.com/issue-management/",
        icon: AlertCircle,
      },
    ],
  },
  {
    category: "Employee Resources",
    items: [
      {
        title: "Trade Pre-Clearance",
        description: "Submit employee trade pre-clearance requests for personal securities transactions",
        url: "https://www.complisun.com/trade-clearance/",
        icon: ClipboardCheck,
      },
      {
        title: "Employee Directory",
        description: "Contact information for Pointe Capital team members",
        url: "https://www.complisun.com/employee-directory-pointe-capital/",
        icon: Users,
      },
      {
        title: "Private Chatroom",
        description: "Secure team communication channel for Pointe Capital employees",
        url: "https://www.complisun.com/chat-for-pointe-capital/",
        icon: MessageSquare,
      },
    ],
  },
  {
    category: "Training & Documentation",
    items: [
      {
        title: "Training Courses",
        description: "Available compliance training courses and certifications",
        url: "https://www.complisun.com/pointe-capital-courses/",
        icon: BookOpen,
      },
      {
        title: "Resources & Core Policies",
        description: "Policy library including code of ethics, procedures, and guidelines",
        url: "https://www.complisun.com/resource-pointe-capital/",
        icon: FolderOpen,
      },
      {
        title: "Compliance Calendar",
        description: "Important compliance dates, deadlines, and regulatory filings",
        url: "https://www.complisun.com/pointe-capital-calendar/",
        icon: Calendar,
      },
    ],
  },
];

export default function ResourcesPage() {
  return (
    <Layout>
      <div className="space-y-8" data-testid="resources-page">
        {/* Header */}
        <div>
          <p className="label-text mb-2">Resources</p>
          <h1 className="heading-xl">Compliance Resources</h1>
          <p className="body-text mt-2 max-w-2xl">
            Access all your compliance tools, training materials, and documentation in one place. 
            All links open in a new tab for your convenience.
          </p>
        </div>

        {/* Resource Categories */}
        {resources.map((category, idx) => (
          <section key={category.category} className={`animate-fade-in-up animation-delay-${(idx + 1) * 100}`}>
            <h2 className="heading-md mb-4">{category.category}</h2>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {category.items.map((item) => {
                const Icon = item.icon;
                return (
                  <a
                    key={item.url}
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`control-card group h-full ${item.featured ? 'border-[#8B0000] bg-red-50/30' : ''}`}
                    data-testid={`resource-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className={`w-12 h-12 rounded flex items-center justify-center mb-4 ${
                      item.featured 
                        ? 'bg-[#8B0000] text-white' 
                        : 'bg-slate-100 text-slate-600 group-hover:bg-[#8B0000] group-hover:text-white'
                    } transition-colors`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex items-start justify-between gap-2">
                      <h3 className={`font-semibold ${item.featured ? 'text-[#8B0000]' : 'text-slate-900 group-hover:text-[#8B0000]'} transition-colors`}>
                        {item.title}
                      </h3>
                      <ExternalLink className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    </div>
                    <p className="text-sm text-slate-500 mt-2">{item.description}</p>
                  </a>
                );
              })}
            </div>
          </section>
        ))}

        {/* Contact Section */}
        <section className="animate-fade-in-up animation-delay-400">
          <Card className="border-slate-200 bg-slate-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-[#8B0000]" />
                Need Help?
              </CardTitle>
              <CardDescription>
                Contact the compliance team for assistance with any questions or concerns
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 border border-slate-200 bg-white">
                  <p className="font-semibold text-slate-900">Chief Compliance Officer</p>
                  <p className="text-sm text-slate-600 mt-1">Colin Darke</p>
                  <a 
                    href="mailto:colindarke@complisun.com" 
                    className="text-sm text-[#8B0000] hover:underline mt-2 inline-block"
                    data-testid="email-cco"
                  >
                    colindarke@complisun.com
                  </a>
                </div>
                <div className="p-4 border border-slate-200 bg-white">
                  <p className="font-semibold text-slate-900">Compliance Team</p>
                  <p className="text-sm text-slate-600 mt-1">General Inquiries</p>
                  <a 
                    href="mailto:pointecapital@complisun.com" 
                    className="text-sm text-[#8B0000] hover:underline mt-2 inline-block"
                    data-testid="email-team"
                  >
                    pointecapital@complisun.com
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </Layout>
  );
}
